import { ComponentProps } from 'react';
import { Ionicons } from '@expo/vector-icons';
import { Href } from 'expo-router';

import { PackVocabulary } from '@/types/activity-pack';
import { Membership } from '@/types/organization';
import { USER_ROLE } from '@/types/user-role';
import { isProviderUser } from '@/utils/user-role';

type IoniconName = ComponentProps<typeof Ionicons>['name'];

export type MobileBottomNavItem = {
  id: string;
  label: string;
  /** Substantivo do ramo que troca o `label` (via `useTerms().title`). */
  term?: keyof PackVocabulary;
  icon: IoniconName;
  iconActive: IoniconName;
  href?: Href;
  action?: 'menu';
  matchPath: (pathname: string) => boolean;
};

function normalizePathname(pathname: string): string {
  return pathname.replace(/\/$/, '') || '/';
}

function matchSegment(pathname: string, segment: string): boolean {
  const normalized = normalizePathname(pathname);
  return normalized === `/${segment}` || normalized.startsWith(`/${segment}/`);
}

export const PROVIDER_BOTTOM_NAV_ITEMS: MobileBottomNavItem[] = [
  {
    id: 'home',
    label: 'Início',
    icon: 'home-outline',
    iconActive: 'home',
    href: '/(tabs)/(provider)/home',
    matchPath: (pathname) => matchSegment(pathname, 'home'),
  },
  {
    id: 'enrollments',
    label: 'Contratos',
    term: 'enrollmentPlural',
    icon: 'document-text-outline',
    iconActive: 'document-text',
    href: '/(tabs)/(provider)/enrollments',
    matchPath: (pathname) => matchSegment(pathname, 'enrollments'),
  },
  {
    id: 'clients',
    label: 'Clientes',
    term: 'clientPlural',
    icon: 'people-outline',
    iconActive: 'people',
    href: '/(tabs)/(provider)/clients',
    matchPath: (pathname) => matchSegment(pathname, 'clients'),
  },
  {
    id: 'charges',
    label: 'Cobranças',
    icon: 'card-outline',
    iconActive: 'card',
    href: '/(tabs)/(provider)/charges',
    matchPath: (pathname) => matchSegment(pathname, 'charges'),
  },
  {
    id: 'menu',
    label: 'Menu',
    icon: 'menu-outline',
    iconActive: 'menu',
    action: 'menu',
    matchPath: () => false,
  },
];

export const CLIENT_BOTTOM_NAV_ITEMS: MobileBottomNavItem[] = [
  {
    id: 'hoje',
    label: 'Início',
    icon: 'grid-outline',
    iconActive: 'grid',
    href: '/(tabs)/(client)/hoje',
    matchPath: (pathname) => matchSegment(pathname, 'hoje'),
  },
  {
    id: 'buscar',
    label: 'Buscar',
    icon: 'search-outline',
    iconActive: 'search',
    href: '/buscar',
    matchPath: (pathname) => matchSegment(pathname, 'buscar'),
  },
  {
    id: 'enrollments-client',
    label: 'Meus Contratos',
    icon: 'document-text-outline',
    iconActive: 'document-text',
    href: '/(tabs)/(client)/enrollments-client',
    matchPath: (pathname) => matchSegment(pathname, 'enrollments-client'),
  },
  {
    id: 'payments',
    label: 'Pagamentos',
    icon: 'card-outline',
    iconActive: 'card',
    href: '/(tabs)/(client)/payments',
    matchPath: (pathname) => matchSegment(pathname, 'payments'),
  },
  {
    id: 'menu',
    label: 'Menu',
    icon: 'menu-outline',
    iconActive: 'menu',
    action: 'menu',
    matchPath: () => false,
  },
];

/**
 * Profissional da equipe: a agenda é o centro do dia dele, e contratos são
 * assunto do dono — Agenda entra no lugar de Contratos.
 */
export const PROFESSIONAL_BOTTOM_NAV_ITEMS: MobileBottomNavItem[] = [
  PROVIDER_BOTTOM_NAV_ITEMS[0],
  {
    id: 'schedule',
    label: 'Agenda',
    icon: 'calendar-outline',
    iconActive: 'calendar',
    href: '/(tabs)/(provider)/schedule' as Href,
    matchPath: (pathname) => matchSegment(pathname, 'schedule'),
  },
  ...PROVIDER_BOTTOM_NAV_ITEMS.slice(2),
];

export function getBottomNavItemsForRole(
  user: { role?: USER_ROLE; providerProfile?: unknown; clientProfile?: unknown } | null,
  membership?: Pick<Membership, 'role'> | null,
): MobileBottomNavItem[] {
  if (!isProviderUser(user)) return CLIENT_BOTTOM_NAV_ITEMS;
  return membership?.role === 'PROFESSIONAL'
    ? PROFESSIONAL_BOTTOM_NAV_ITEMS
    : PROVIDER_BOTTOM_NAV_ITEMS;
}

export function getActiveBottomNavIndex(pathname: string, items: MobileBottomNavItem[]): number {
  const index = items.findIndex((item) => item.matchPath(pathname));
  return index >= 0 ? index : -1;
}

/** Telas de criação: o formulário ocupa a tela inteira, sem bottom nav. */
const FORM_PATHS = [
  '/enrollments/new',
  '/services/create',
  '/schedule/new-appointment',
  '/profile/post-generator',
  '/payments/activate',
];

/**
 * Formulário de criar/editar esconde o bottom nav — sair por ele no meio do
 * preenchimento perderia o que foi digitado. Editar reusa a rota de detalhe
 * com `?edit=1` (contrato e serviço).
 */
export function isBottomNavHiddenRoute(pathname: string, edit?: string | string[]): boolean {
  const normalized = normalizePathname(pathname);
  if (FORM_PATHS.includes(normalized)) return true;
  const isEditing = (Array.isArray(edit) ? edit[0] : edit) === '1';
  return (
    isEditing && (matchSegment(normalized, 'enrollments') || matchSegment(normalized, 'services'))
  );
}
